import { useState, useCallback } from 'react';

const IMAGE_KEY = 'alalemeya_product_images';
const DATA_KEY = 'alalemeya_product_data';

interface DataOverride {
  price?: number;
  name?: string;
}

function getStored<T>(key: string): Record<string, T> {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : {};
  } catch {
    return {};
  }
}

export function useProductImages() {
  const [imageOverrides, setImageOverrides] = useState<Record<string, string>>(() =>
    getStored<string>(IMAGE_KEY)
  );
  const [dataOverrides, setDataOverrides] = useState<Record<string, DataOverride>>(() =>
    getStored<DataOverride>(DATA_KEY)
  );

  const updateImage = useCallback((productId: string, dataUrl: string) => {
    setImageOverrides(prev => {
      const next = { ...prev, [productId]: dataUrl };
      localStorage.setItem(IMAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const updateData = useCallback((productId: string, data: DataOverride) => {
    setDataOverrides(prev => {
      const next = { ...prev, [productId]: { ...prev[productId], ...data } };
      localStorage.setItem(DATA_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const getImage = useCallback(
    (productId: string, defaultImage: string): string =>
      imageOverrides[productId] || defaultImage,
    [imageOverrides]
  );

  const getPrice = useCallback(
    (productId: string, defaultPrice: number): number =>
      dataOverrides[productId]?.price ?? defaultPrice,
    [dataOverrides]
  );

  const getName = useCallback(
    (productId: string, defaultName: string): string =>
      dataOverrides[productId]?.name ?? defaultName,
    [dataOverrides]
  );

  return { getImage, updateImage, getPrice, getName, updateData };
}
